import * as THREE from 'three'
import gsap from 'gsap'

/**
 * initCards — manages all tarot card objects, textures, animations, and hover effects.
 *
 * @param {THREE.Scene}        scene
 * @param {THREE.TextureLoader} loader
 * @param {THREE.Raycaster}    raycaster   — shared from particles.js
 * @param {THREE.Vector2}      mouseCoords — shared from particles.js, updated by spawnDust
 * @param {object}             sounds      — sounds object from sounds.js
 * @param {function}           onTexturesLoaded — called when all 78 card textures are ready
 *
 * @returns {{ cards, cardGlows, CARD_COUNT,
 *             preloadTextures, shuffle, revealCard, revealDailyCard,
 *             typeWriter, resetCards, animateCards, handleCardHover,
 *             getRevealed, setRevealed }}
 */
export function initCards(scene, loader, raycaster, mouseCoords, sounds, onTexturesLoaded) {

    // ── Constants & shared state ──────────────────────────────────────────────
    const CARD_COUNT = 78
    const cards = []
    const textureCache = {}
    let loadedCount = 0
    let revealed = false
    let fanParam = { progress: 0 }

    // ── Texture loading ───────────────────────────────────────────────────────
    const cardBackTexture = loader.load('./textures/taro/back_new2.webp')
    cardBackTexture.colorSpace = THREE.SRGBColorSpace

    function preloadTextures() {
        const loaderText = document.getElementById('loader-text')

        for (let i = 0; i < CARD_COUNT; i++) {
            loader.load(
                `./textures/taro/${i}.webp`,
                (texture) => {
                    texture.colorSpace = THREE.SRGBColorSpace
                    texture.repeat.x = -1
                    texture.offset.x = 1
                    textureCache[i] = texture
                    loadedCount++

                    const percent = Math.floor((loadedCount / CARD_COUNT) * 100)
                    if (loaderText) loaderText.innerText = `Charging the magic deck... ${percent}%`

                    if (loadedCount === CARD_COUNT) {
                        gsap.to(document.getElementById('loader-screen'), {
                            opacity: 0,
                            duration: 0.8,
                            onComplete: () => {
                                document.getElementById('loader-screen').style.display = 'none'
                                onTexturesLoaded()
                            }
                        })
                        document.getElementById('ask').disabled = false
                    }
                },
                undefined,
                (err) => console.error(`Error loading card ${i}:`, err)
            )
        }
    }

    // ── Gold wireframe ShaderMaterial ─────────────────────────────────────────
    const wireframeMaterial = new THREE.ShaderMaterial({
        transparent: true,
        wireframe: true,
        depthWrite: false,
        uniforms: {
            uMouse:     { value: new THREE.Vector3() },
            uRadius:    { value: 0.15 },
            uTime:      { value: 0.0 },
            uSharpness: { value: 0.1 },
            uColor:     { value: new THREE.Color(0xffd700) },
            uOpacity:   { value: 0.0 }
        },
        vertexShader: `
            varying vec3 vPosition;
            varying vec2 vUv;
            void main() {
                vPosition = position;
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            varying vec3 vPosition;
            uniform vec3 uMouse;
            uniform float uRadius;
            uniform float uTime;
            uniform vec3 uColor;
            uniform float uOpacity;

            vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
            vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
            vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

            float snoise(vec2 v) {
                const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
                vec2 i  = floor(v + dot(v, C.yy));
                vec2 x0 = v - i + dot(i, C.xx);
                vec2 i1;
                i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
                vec4 x12 = x0.xyxy + C.xxzz;
                x12.xy -= i1;
                i = mod289(i);
                vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
                vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
                m = m*m; m = m*m;
                vec3 x = 2.0 * fract(p * C.www) - 1.0;
                vec3 h = abs(x) - 0.5;
                vec3 a0 = x - floor(x + 0.5);
                vec3 g = a0.xyx * vec3(x0.x, x12.xz) + h.xyx * vec3(x0.y, x12.yw);
                return 130.0 * dot(m, g);
            }

            void main() {
                float dist = distance(vPosition, uMouse);
                float noiseScale = 3.0;
                float noiseInfluence = 0.15;
                float n = snoise(vPosition.xy * noiseScale + uTime * 0.5);
                float distortedRadius = uRadius + (n * noiseInfluence);
                float mask = 1.0 - smoothstep(distortedRadius - 0.05, distortedRadius, dist);
                float edgeWidth = 0.01;
                float line = smoothstep(distortedRadius - edgeWidth, distortedRadius, dist)
                           * (1.0 - smoothstep(distortedRadius, distortedRadius + edgeWidth, dist));
                vec3 finalColor = mix(uColor, uColor + vec3(0.4, 0.4, 0.0), line * 2.0);
                float finalAlpha = max(mask * 0.4, line * 4.0) * uOpacity;
                if (finalAlpha < 0.01) discard;
                gl_FragColor = vec4(finalColor, finalAlpha);
            }
        `
    })

    // ── Geometry & card meshes ────────────────────────────────────────────────
    const geometry = new THREE.PlaneGeometry(1, 1.75, 20, 30)

    for (let i = 0; i < CARD_COUNT; i++) {
        const mat = new THREE.MeshBasicMaterial({
            map: cardBackTexture,
            side: THREE.DoubleSide,
            transparent: true
        })
        const card = new THREE.Mesh(geometry, mat)
        card.position.set(0, 1, i * 0.002)
        card.scale.set(1.5, 1.5, 1.5)
        card.userData = { id: i, isHero: false }

        const uniqueWireMaterial = wireframeMaterial.clone()
        const wireMesh = new THREE.Mesh(card.geometry, uniqueWireMaterial)
        wireMesh.scale.set(1.001, 1.001, 1.001)
        wireMesh.name = "goldWireframe"
        card.add(wireMesh)

        cards.push(card)
        scene.add(card)
    }

    // ── Glow sprites ──────────────────────────────────────────────────────────
    function createGlowTexture() {
        const canvas = document.createElement('canvas')
        canvas.width = 64
        canvas.height = 64
        const ctx = canvas.getContext('2d')
        const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
        gradient.addColorStop(0,   'rgba(255, 215, 0, 1)')
        gradient.addColorStop(0.2, 'rgba(255, 165, 0, 0.4)')
        gradient.addColorStop(1,   'rgba(0, 0, 0, 0)')
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, 64, 64)
        return new THREE.CanvasTexture(canvas)
    }

    const glowTexture = createGlowTexture()
    const cardGlows = []

    for (let i = 0; i < 3; i++) {
        const glowMaterial = new THREE.SpriteMaterial({
            map: glowTexture,
            color: 0xffd700,
            transparent: true,
            opacity: 0,
            blending: THREE.AdditiveBlending
        })
        const glow = new THREE.Sprite(glowMaterial)
        glow.scale.set(1.5, 1.5, 1)
        glow.visible = false
        scene.add(glow)
        cardGlows.push(glow)
    }

    // ── Card hover (wireframe ripple) ─────────────────────────────────────────
    // Call this from the main mousemove handler AFTER spawnDust has updated mouseCoords.
    function handleCardHover(camera) {
        raycaster.setFromCamera(mouseCoords, camera)
        const intersects = raycaster.intersectObjects(cards)

        if (intersects.length > 0) {
            const hit = intersects[0]
            const hoveredCard = hit.object
            const wire = hoveredCard.getObjectByName("goldWireframe")
            if (wire) {
                wire.visible = true
                const localPoint = hoveredCard.worldToLocal(hit.point.clone())
                wire.material.uniforms.uMouse.value.copy(localPoint)
                gsap.to(wire.material.uniforms.uOpacity, { value: 1.0, duration: 0.4, overwrite: true })
            }
        }

        cards.forEach(card => {
            const wire = card.getObjectByName("goldWireframe")
            const isHovered = intersects.length > 0 && intersects[0].object === card
            if (wire && !isHovered && wire.visible) {
                gsap.to(wire.material.uniforms.uOpacity, {
                    value: 0.0, duration: 0.6, overwrite: true,
                    onComplete: () => { if (!isHovered) wire.visible = false }
                })
            }
        })
    }

    const _onTouchEnd = () => {
        cards.forEach(card => {
            const wire = card.getObjectByName("goldWireframe")
            if (wire) {
                gsap.to(wire.material.uniforms.uOpacity, {
                    value: 0.0, duration: 0.8,
                    onComplete: () => { wire.visible = false }
                })
            }
        })
    }
    window.addEventListener('touchend', _onTouchEnd)

    // ── Shuffle animation ─────────────────────────────────────────────────────
    function shuffle() {
        setTimeout(() => {
            sounds.shuffle.currentTime = 0
            sounds.shuffle.play()
        }, 200)
        revealed = false

        cards.forEach(card => {
            gsap.killTweensOf(card.position)
            gsap.killTweensOf(card.rotation)
            gsap.killTweensOf(card.scale)
            gsap.killTweensOf(card.material)

            card.userData.canFloat = false

            if (card.userData.flipped) {
                gsap.to(card.rotation, {
                    y: 0,
                    duration: 0.8,
                    ease: "power2.inOut",
                    onUpdate: () => {
                        if (Math.abs(card.rotation.y) < Math.PI / 2) {
                            card.material.map = cardBackTexture
                            card.userData.flipped = false
                        }
                    }
                })
            }

            if (card.material.opacity < 1) {
                gsap.to(card.material, { opacity: 1, duration: 0.8, ease: "power2.out" })
            }
        })

        const tl = gsap.timeline()
        const total = cards.length
        const fanAngle = Math.PI * 0.7
        const radius = 2.0
        const centerY = 1.0

        cards.forEach((card, i) => {
            const angle = (i / total - 0.5) * fanAngle
            tl.to(card.position, {
                x: Math.sin(angle) * radius,
                y: centerY + Math.cos(angle) * (radius * 0.4),
                z: i * 0.005,
                duration: 0.8,
                ease: "expo.out"
            }, i * 0.002)
            tl.to(card.rotation, { z: -angle, duration: 0.8, ease: "expo.out" }, "<")
            tl.to(card.scale, { x: 1.5, y: 1.5, z: 1.5, duration: 0.8 }, "<")
        })

        cards.forEach((card, i) => {
            const dropDelay = 1.0 + (i * 0.004)
            tl.to(card.position, { x: 0, y: 1, z: i * 0.002, duration: 0.6, ease: "power3.inOut" }, dropDelay)
            tl.to(card.rotation, { x: 0, y: 0, z: 0, duration: 0.6, ease: "power3.inOut" }, dropDelay)
        })

        document.getElementById('ui').classList.remove('ui-hidden')
        return tl
    }

    // ── typeWriter ────────────────────────────────────────────────────────────
    function typeWriter(text, elementId) {
        const element = document.getElementById(elementId)
        element.innerHTML = ""
        let i = 0
        const speed = 30
        function type() {
            if (i < text.length) {
                element.innerHTML += text.charAt(i)
                i++
                setTimeout(type, speed)
            }
        }
        type()
    }

    // ── Reveal daily card (1 card) ────────────────────────────────────────────
    function revealDailyCard(cardId, prediction) {
        window.lastChosenIds = [cardId]
        window.currentPrediction = prediction

        const meaningBlock = document.getElementById('meaning-container')
        meaningBlock.style.webkitOverflowScrolling = 'touch'
        meaningBlock.addEventListener('touchstart', e => e.stopPropagation(), { passive: true })
        meaningBlock.addEventListener('touchmove',  e => e.stopPropagation(), { passive: true })

        const chosenIds = window.lastChosenIds
        if (!chosenIds || chosenIds.length === 0) return

        const id   = chosenIds[0]
        const card = cards[id]
        const glow = cardGlows[0]

        const wire = card.getObjectByName("goldWireframe")
        if (wire) { wire.visible = false; wire.material.uniforms.uOpacity.value = 0 }

        const shouldFlip = !window.isLimitReachedNow

        document.getElementById('ui').classList.add('ui-hidden')

        const isMobile  = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const cardScale = isMobile ? 0.6 : 0.85
        const targetX   = 0
        const targetY   = isMobile ? 1.7 : 1.6
        const targetZ   = 2.5

        glow.visible = false
        glow.material.opacity = 0
        card.renderOrder = 1000

        gsap.killTweensOf(card.position)
        gsap.killTweensOf(card.rotation)

        const tl = gsap.timeline()

        tl.to(card.position, { x: targetX, y: targetY, z: targetZ, duration: 1.2, ease: "expo.out" })
          .to(card.scale,    { x: cardScale, y: cardScale, z: cardScale, duration: 1.2 }, "-=1.2")

        if (shouldFlip) {
            tl.to(card.rotation, {
                y: -Math.PI,
                duration: 1.4,
                onUpdate: () => {
                    if (Math.abs(card.rotation.y) > Math.PI / 2 && !card.userData.flipped) {
                        card.material.map = textureCache[id]
                        card.userData.flipped = true
                    }
                },
                onComplete: () => {
                    card.userData.basePosition  = { x: targetX, y: targetY, z: targetZ }
                    card.userData.baseRotationY = -Math.PI
                    card.userData.canFloat = true

                    glow.position.set(card.position.x, card.position.y, targetZ - 0.2)
                    glow.visible = true
                    gsap.to(glow.material, { opacity: 0.7, duration: 2, ease: "sine.inOut" })
                }
            }, "-=0.7")
        } else {
            tl.add(() => {
                card.userData.flipped = false
                card.userData.basePosition  = { x: targetX, y: targetY, z: targetZ }
                card.userData.baseRotationY = 0
                card.userData.canFloat = true
            })
        }

        cards.forEach((c, idx) => {
            if (idx !== id) {
                gsap.to(c.position, { y: -5, duration: 1.5, delay: 0.2, ease: "power2.inOut" })
                gsap.to(c.material, { opacity: 0, duration: 1.2, delay: 0.2, ease: "linear" })
            }
        })

        meaningBlock.style.display = 'flex'
        gsap.to(meaningBlock, {
            opacity: 0.9,
            pointerEvents: 'auto',
            duration: 0.8,
            delay: shouldFlip ? 1.8 : 0.8,
            onStart: () => {
                gsap.to("#questionDisplay", { opacity: 1, duration: 1 })
                const targetElement = document.getElementById('meaning-text')
                targetElement.innerHTML = ''
                typeWriter(window.currentPrediction || "...", 'meaning-text')
            }
        })
    }

    // ── Reveal 3 cards ────────────────────────────────────────────────────────
    function revealCard() {
        const meaningBlock = document.getElementById('meaning-container')
        meaningBlock.style.webkitOverflowScrolling = 'touch'
        meaningBlock.addEventListener('touchstart', e => e.stopPropagation(), { passive: true })
        meaningBlock.addEventListener('touchmove',  e => e.stopPropagation(), { passive: true })

        const chosenIds = window.lastChosenIds
        const textContent = window.currentPrediction || ""
        const isLimitMessage = textContent.toLowerCase().includes("limit")
        const shouldFlip = !window.isLimitReachedNow && !isLimitMessage

        document.getElementById('ui').classList.add('ui-hidden')

        const isMobile  = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const xDist     = isMobile ? 0.5 : 1.2
        const cardScale = isMobile ? 0.4 : 0.65
        const xOffsets  = [-xDist, 0, xDist]
        const yOffsets  = isMobile ? [1.6, 1.7, 1.6] : [1.6, 1.7, 1.6]

        chosenIds.forEach((id, index) => {
            const card = cards[id]
            const glow = cardGlows[index]

            glow.visible = false
            glow.material.opacity = 0
            card.renderOrder = 990 + index

            const wire = card.getObjectByName("goldWireframe")
            if (wire) { wire.visible = false; wire.material.uniforms.uOpacity.value = 0 }

            gsap.killTweensOf(card.position)
            gsap.killTweensOf(card.rotation)

            const tl = gsap.timeline()
            tl.to(card.position, {
                x: xOffsets[index], y: yOffsets[index], z: 2.5,
                duration: 0.9, delay: index * 0.2, ease: "expo.out"
            })
            .to(card.scale, { x: cardScale, y: cardScale, z: cardScale, duration: 0.9 }, "-=0.9")

            if (shouldFlip) {
                tl.to(card.rotation, {
                    y: -Math.PI,
                    duration: 1.2,
                    onUpdate: () => {
                        if (Math.abs(card.rotation.y) > Math.PI / 2 && !card.userData.flipped) {
                            card.material.map = textureCache[id]
                            card.userData.flipped = true
                        }
                    },
                    onComplete: () => {
                        card.userData.basePosition  = { x: xOffsets[index], y: yOffsets[index], z: 2.5 }
                        card.userData.baseRotationY = -Math.PI
                        card.userData.canFloat = true

                        glow.position.set(card.position.x, card.position.y, 2.3)
                        glow.visible = true
                        gsap.to(glow.material, { opacity: 0.6, duration: 1.5, ease: "sine.inOut" })
                    }
                }, "-=0.6")
            } else {
                tl.add(() => {
                    card.userData.flipped = false
                    card.userData.basePosition  = { x: xOffsets[index], y: yOffsets[index], z: 2.5 }
                    card.userData.baseRotationY = 0
                    card.userData.canFloat = true
                })
            }
        })

        cards.forEach((c, idx) => {
            if (!chosenIds.includes(idx)) {
                gsap.to(c.position, { y: -5, duration: 1.5, delay: 0.5, ease: "power2.inOut" })
                gsap.to(c.material, { opacity: 0, duration: 1.2, delay: 0.5, ease: "linear" })
            }
        })

        meaningBlock.style.display = 'flex'
        gsap.to(meaningBlock, {
            opacity: 1,
            pointerEvents: 'auto',
            duration: 0.8,
            delay: shouldFlip ? 2.0 : 1.0,
            onStart: () => {
                gsap.to("#questionDisplay", { opacity: 1, duration: 1 })
                const targetElement = document.getElementById('meaning-text')
                targetElement.innerHTML = ''
                typeWriter(window.currentPrediction || "The stars are silent. Please try again.", 'meaning-text')
            }
        })
    }

    // ── resetCards — 3-D collect animation (called by resetUI in script.js) ──
    function resetCards(chosenIds) {
        if (!chosenIds) return

        // Disable levitation before animating
        chosenIds.forEach(id => {
            cards[id].userData.canFloat = false
        })

        revealed = false

        const tl = gsap.timeline()

        // 1. Fade out glows
        cardGlows.forEach(glow => {
            tl.to(glow.material, {
                opacity: 0, duration: 0.4, ease: "power2.out",
                onComplete: () => { glow.visible = false }
            }, 0)
        })

        const cardMoveDelay = 0.4

        // 2. Flip chosen cards back & collect
        chosenIds.forEach((id) => {
            const card = cards[id]
            card.userData.canFloat = false
            gsap.killTweensOf(card.position)
            gsap.killTweensOf(card.rotation)

            const wasFlipped = card.userData.flipped === true

            if (wasFlipped) {
                tl.to(card.rotation, {
                    x: 0, y: 0, z: 0,
                    duration: 0.8,
                    ease: "power2.inOut",
                    onUpdate: () => {
                        if (Math.abs(card.rotation.y) < Math.PI / 2) {
                            card.material.map = cardBackTexture
                            card.userData.flipped = false
                        }
                    }
                }, cardMoveDelay)
            }

            tl.to(card.scale, { x: 1.5, y: 1.5, z: 1.5, duration: 0.8, ease: "power2.inOut" }, cardMoveDelay)

            const finalMoveStart = wasFlipped ? cardMoveDelay + 0.6 : cardMoveDelay

            tl.add(() => {
                sounds.final.currentTime = 0
                sounds.final.play()
            }, finalMoveStart)

            cards.forEach((c, i) => {
                tl.to(c.position, {
                    x: 0, y: 1, z: 0 + (i * 0.01),
                    duration: 0.9, ease: "power2.inOut"
                }, finalMoveStart)
            })
        })

        const anyFlipped = chosenIds.some(id => cards[id].userData.flipped)
        const fanStartPoint = anyFlipped ? cardMoveDelay + 1.8 : cardMoveDelay + 1.2
        tl.add("fanOpen", fanStartPoint)

        // 3. Restore remaining deck opacity
        const remainingCards = cards.filter((_, idx) => !chosenIds.includes(idx))
        remainingCards.forEach((card, i) => {
            gsap.set(card.position, { x: 0, y: 1, z: i * 0.002 })
            gsap.set(card.rotation, { x: 0, y: 0, z: 0 })
            gsap.set(card.scale,    { x: 1.5, y: 1.5, z: 1.5 })
            gsap.set(card.material, { opacity: 0 })
        })
        tl.to(remainingCards.map(c => c.material), {
            opacity: 1, duration: 0.5, stagger: 0.002
        }, cardMoveDelay + 0.4)

        // 4. Mini fan open
        const fanCount  = 7
        const total     = cards.length
        const topCards  = cards.slice(total - fanCount)
        topCards.forEach(card => { card.userData.isFanning = true })

        const fanLabel = "fanOpen"
        tl.add(fanLabel, cardMoveDelay + 2.9)

        tl.to(fanParam, {
            progress: 1, duration: 0.8, ease: "sine.inOut",
            onUpdate: () => {
                topCards.forEach((card, i) => {
                    const p         = fanParam.progress
                    const baseAngle = (fanCount - 1 - i) * 0.15
                    const curAngle  = baseAngle * p
                    const pivotY    = -0.875
                    const offsetX   = -pivotY * Math.sin(curAngle)
                    const offsetY   = pivotY  * (Math.cos(curAngle) - 1)
                    card.position.x = -(fanCount - 1 - i) * 0.25 * p + offsetX
                    card.position.y = 1 + offsetY
                    card.position.z = (total - fanCount + i) * 0.002
                    card.rotation.z = curAngle
                })
            }
        }, fanLabel)

        // 5. Mini fan close
        const closeLabel = "fanClose"
        tl.add(closeLabel, "+=0.5")

        tl.add(() => {
            sounds.final2.currentTime = 0
            sounds.final2.play()
        }, `${closeLabel}-=0.35`)

        tl.to(fanParam, {
            progress: 0, duration: 0.6, ease: "power2.inOut",
            onUpdate: () => {
                topCards.forEach((card, i) => {
                    const p         = fanParam.progress
                    const baseAngle = (fanCount - 1 - i) * 0.15
                    const curAngle  = baseAngle * p
                    const pivotY    = -0.875
                    const offsetX   = -pivotY * Math.sin(curAngle)
                    const offsetY   = pivotY  * (Math.cos(curAngle) - 1)
                    card.position.x = -(fanCount - 1 - i) * 0.25 * p + offsetX
                    card.position.y = 1 + offsetY
                    card.position.z = (total - fanCount + i) * 0.002
                    card.rotation.z = curAngle
                })
            },
            onComplete: () => {
                topCards.forEach(card => { card.userData.isFanning = false })
            }
        }, "+=0.2", closeLabel)

        window.lastChosenIds = null
    }

    // ── animateCards — call every frame from the main animate() loop ──────────
    function animateCards(elapsedTime) {
        // 1. Levitate chosen (hero) cards
        if (window.lastChosenIds) {
            window.lastChosenIds.forEach((id, index) => {
                const card = cards[id]
                const glow = cardGlows[index]
                if (card && card.userData.canFloat && card.userData.basePosition) {
                    const t      = elapsedTime * 1.5 + index
                    const tgtY   = card.userData.basePosition.y + Math.sin(t) * 0.05
                    const tgtZ   = card.userData.basePosition.z + Math.cos(t * 0.7) * 0.02
                    const tgtRZ  = Math.sin(elapsedTime * 0.8 + index) * 0.03
                    const tgtRY  = card.userData.baseRotationY + Math.sin(t * 0.5) * 0.04
                    card.position.y  += (tgtY  - card.position.y)  * 0.1
                    card.position.z  += (tgtZ  - card.position.z)  * 0.1
                    card.rotation.z  += (tgtRZ - card.rotation.z)  * 0.1
                    card.rotation.y  += (tgtRY - card.rotation.y)  * 0.1
                    if (glow && glow.visible) {
                        glow.position.copy(card.position)
                        glow.position.z -= 0.2
                    }
                }
            })
        }

        // 2. Update wireframe shader time
        const time = performance.now() * 0.0001
        cards.forEach(card => {
            const wire = card.getObjectByName("goldWireframe")
            if (wire && wire.visible) wire.material.uniforms.uTime.value = time
        })

        // 3. Idle deck levitation
        cards.forEach((card, i) => {
            const isBusy =
                card.userData.canFloat ||
                card.userData.isFanning ||
                gsap.isTweening(card.position) ||
                gsap.isTweening(fanParam)

            if (!revealed && !isBusy) {
                const t     = elapsedTime * 2 + i * 0.02
                const waitY = 1 + Math.sin(t) * 0.05
                card.position.y += (waitY - card.position.y) * 0.05
                card.rotation.z += (0 - card.rotation.z) * 0.05
            }
        })
    }

    // ── Destroy ───────────────────────────────────────────────────────────────
    function destroy() {
        window.removeEventListener('touchend', _onTouchEnd)

        // Dispose card meshes + wireframe clones
        cards.forEach(card => {
            card.material.dispose()
            const wire = card.getObjectByName("goldWireframe")
            if (wire) wire.material.dispose()
            scene.remove(card)
        })

        // Dispose glow sprites
        cardGlows.forEach(glow => {
            glow.material.dispose()
            scene.remove(glow)
        })

        // Dispose shared geometry
        geometry.dispose()
        wireframeMaterial.dispose()

        // Dispose textures
        cardBackTexture.dispose()
        glowTexture.dispose()
        Object.values(textureCache).forEach(t => t.dispose())
    }

    // ── Public API ────────────────────────────────────────────────────────────
    return {
        cards,
        cardGlows,
        CARD_COUNT,
        preloadTextures,
        shuffle,
        revealCard,
        revealDailyCard,
        typeWriter,
        resetCards,
        animateCards,
        handleCardHover,
        getRevealed: ()  => revealed,
        setRevealed: (v) => { revealed = v },
        destroy
    }
}
